/**
 * Timezone utility functions for consistent time handling
 */

import logger from '../services/logger';

/**
 * Convert UTC timestamp to local timezone with proper formatting
 * @param {string|Date} timestamp - UTC timestamp (ISO string or Date object)
 * @param {string} timezone - Target timezone (default: 'Europe/Istanbul')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatTimestamp = (timestamp, timezone = 'Europe/Istanbul', options = {}) => {
  if (!timestamp) return 'N/A';
  
  try {
    // Ensure we have a proper Date object
    let date;
    if (typeof timestamp === 'string') {
      // Handle different timestamp formats
      let isoString = timestamp;
      
      // If it's already a valid ISO string with timezone, use as is
      if (timestamp.includes('+') || timestamp.endsWith('Z')) {
        isoString = timestamp;
      } else {
        // If it's a date without timezone info, assume it's UTC
        isoString = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
      }
      
      date = new Date(isoString);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return 'Geçersiz Tarih';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // Try to parse as a different format
      try {
        // Try parsing as a simple date string
        const fallbackDate = new Date(timestamp);
        if (!isNaN(fallbackDate.getTime())) {
          date = fallbackDate;
        } else {
          return 'Geçersiz Tarih';
        }
      } catch (fallbackError) {
        return 'Geçersiz Tarih';
      }
    }
    
    // Check if date is too old (before 1970) or too far in future
    const year = date.getFullYear();
    if (year < 1970 || year > 2100) {
      return 'Geçersiz Tarih';
    }
    
    // Default formatting options
    const defaultOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone,
      hour12: false
    };
    
    // Merge with provided options
    const formatOptions = { ...defaultOptions, ...options };
    
    return new Intl.DateTimeFormat('tr-TR', formatOptions).format(date);
  } catch (error) {
    logger.error('Error formatting timestamp:', error, 'Input:', timestamp);
    return 'Tarih Hatası';
  }
};

/**
 * Get relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} timestamp - UTC timestamp
 * @param {string} timezone - Target timezone
 * @returns {string} Relative time string
 */
export const getRelativeTime = (timestamp, timezone = 'Europe/Istanbul') => {
  if (!timestamp) return 'N/A';
  
  try {
    let date;
    if (typeof timestamp === 'string') {
      // Handle different timestamp formats
      let isoString = timestamp;
      
      // If it's already a valid ISO string with timezone, use as is
      if (timestamp.includes('+') || timestamp.endsWith('Z')) {
        isoString = timestamp;
      } else {
        // If it's a date without timezone info, assume it's UTC
        isoString = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
      }
      
      date = new Date(isoString);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return 'Geçersiz Tarih';
    }
    
    if (isNaN(date.getTime())) {
      // Try fallback parsing
      try {
        const fallbackDate = new Date(timestamp);
        if (!isNaN(fallbackDate.getTime())) {
          date = fallbackDate;
        } else {
          return 'Geçersiz Tarih';
        }
      } catch (fallbackError) {
        return 'Geçersiz Tarih';
      }
    }
    
    // Check if date is reasonable
    const year = date.getFullYear();
    if (year < 1970 || year > 2100) {
      return 'Geçersiz Tarih';
    }
    
    const now = new Date();
    const diffMs = now - date;

    // Sunucu-tarayıcı saat farkı toleransı (60 saniye)
    // Sunucu saati tarayıcıdan birkaç saniye ilerideyse "Gelecekte" yerine "Az önce" göster
    if (diffMs < -60000) {
      return 'Gelecekte';
    }

    const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSeconds < 60) {
      return 'Az önce';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} dakika önce`;
    } else if (diffHours < 24) {
      return `${diffHours} saat önce`;
    } else if (diffDays < 7) {
      return `${diffDays} gün önce`;
    } else {
      return formatTimestamp(timestamp, timezone, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  } catch (error) {
    logger.error('Error getting relative time:', error, 'Input:', timestamp);
    return 'Tarih Hatası';
  }
};

/**
 * Check if timestamp is recent (within specified minutes)
 * @param {string|Date} timestamp - UTC timestamp
 * @param {number} minutes - Threshold in minutes (default: 5)
 * @returns {boolean} True if recent
 */
export const isRecent = (timestamp, minutes = 5) => {
  if (!timestamp) return false;
  
  try {
    let date;
    if (typeof timestamp === 'string') {
      // Handle different timestamp formats
      let isoString = timestamp;
      
      // If it's already a valid ISO string with timezone, use as is
      if (timestamp.includes('+') || timestamp.endsWith('Z')) {
        isoString = timestamp;
      } else {
        // If it's a date without timezone info, assume it's UTC
        isoString = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
      }
      
      date = new Date(isoString);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return false;
    }
    
    if (isNaN(date.getTime())) {
      // Try fallback parsing
      try {
        const fallbackDate = new Date(timestamp);
        if (!isNaN(fallbackDate.getTime())) {
          date = fallbackDate;
        } else {
          return false;
        }
      } catch (fallbackError) {
        return false;
      }
    }
    
    // Check if date is reasonable
    const year = date.getFullYear();
    if (year < 1970 || year > 2100) {
      return false;
    }
    
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    return diffMinutes <= minutes && diffMs >= 0; // Not in the future
  } catch (error) {
    logger.error('Error checking if recent:', error, 'Input:', timestamp);
    return false;
  }
};

/**
 * Get current UTC timestamp as ISO string
 * @returns {string} Current UTC timestamp
 */
export const getCurrentUTCTimestamp = () => {
  return new Date().toISOString();
};

/**
 * Convert local time to UTC timestamp
 * @param {Date} localDate - Local date object
 * @returns {string} UTC timestamp
 */
export const toUTCTimestamp = (localDate) => {
  return localDate.toISOString();
};

/**
 * Parse timestamp and ensure it's in UTC format
 * @param {string|Date} timestamp - Input timestamp
 * @returns {string} UTC ISO string
 */
export const ensureUTC = (timestamp) => {
  if (!timestamp) return null;
  
  try {
    let date;
    if (typeof timestamp === 'string') {
      // Handle different timestamp formats
      let isoString = timestamp;
      
      // If it's already a valid ISO string with timezone, use as is
      if (timestamp.includes('+') || timestamp.endsWith('Z')) {
        isoString = timestamp;
      } else {
        // If it's a date without timezone info, assume it's UTC
        isoString = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
      }
      
      date = new Date(isoString);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return null;
    }
    
    if (isNaN(date.getTime())) {
      // Try fallback parsing
      try {
        const fallbackDate = new Date(timestamp);
        if (!isNaN(fallbackDate.getTime())) {
          date = fallbackDate;
        } else {
          return null;
        }
      } catch (fallbackError) {
        return null;
      }
    }

    return date.toISOString();
  } catch (error) {
    logger.error('Error ensuring UTC:', error, 'Input:', timestamp);
    return null;
  }
};

/**
 * Safe timestamp formatter that handles invalid dates gracefully
 * @param {string|Date} timestamp - Input timestamp
 * @param {string} timezone - Target timezone
 * @param {object} options - Formatting options
 * @returns {string} Formatted date or fallback text
 */
export const safeFormatTimestamp = (timestamp, timezone = 'Europe/Istanbul', options = {}) => {
  const formatted = formatTimestamp(timestamp, timezone, options);
  
  // If formatting failed, return a user-friendly message
  if (formatted === 'Geçersiz Tarih' || formatted === 'Tarih Hatası') {
    return 'Tarih Bilgisi Yok';
  }
  
  return formatted;
};

/**
 * Safe date-only formatter (saat gösterilmez)
 * @param {string|Date} timestamp - Input timestamp
 * @param {string} timezone - Target timezone
 * @returns {string} Formatted date (gün.ay.yıl) or fallback text
 */
export const safeFormatDate = (timestamp, timezone = 'Europe/Istanbul') => {
  return safeFormatTimestamp(timestamp, timezone, {
    hour: undefined,
    minute: undefined,
    second: undefined
  });
};

/**
 * Safe relative time formatter that handles invalid dates gracefully
 * @param {string|Date} timestamp - Input timestamp
 * @param {string} timezone - Target timezone
 * @returns {string} Relative time or fallback text
 */
export const safeGetRelativeTime = (timestamp, timezone = 'Europe/Istanbul') => {
  const relative = getRelativeTime(timestamp, timezone);
  
  // If relative time failed, return a user-friendly message
  if (relative === 'Geçersiz Tarih' || relative === 'Tarih Hatası') {
    return 'Tarih Bilgisi Yok';
  }
  
  return relative;
};
